'use client'

import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import type React from 'react'
import { createContext, useContext, useMemo, useState } from 'react'

import { Button } from '../Button'
import { Link } from '../Link'

type SearchableDropdownContextValue = {
  normalizedQuery: string
  query: string
  setQuery: (q: string) => void
}

const SearchableDropdownContext = createContext<SearchableDropdownContextValue | null>(null)

function useSearchableDropdown() {
  return useContext(SearchableDropdownContext)
}

export const Dropdown = (props: Headless.MenuProps) => {
  return <Headless.Menu {...props} />
}

export const DropdownButton = <T extends React.ElementType = typeof Button>({
  as = Button,
  ...props
}: { className?: string } & Omit<Headless.MenuButtonProps<T>, 'className'>) => {
  return (
    <Headless.MenuButton
      as={as}
      {...props}
    />
  )
}

export const DropdownMenu = ({
  anchor = 'bottom',
  className,
  searchable = false,
  searchPlaceholder = 'Search…',
  ...props
}: {
  className?: string
  searchable?: boolean
  searchPlaceholder?: string
} & Omit<Headless.MenuItemsProps, 'as' | 'className'>) => {
  const [query, setQuery] = useState('')
  const contextValue = useMemo<SearchableDropdownContextValue | null>(() => {
    if (!searchable) {
      return null
    }

    return { query, normalizedQuery: query.trim().toLowerCase(), setQuery }
  }, [searchable, query])

  return (
    <Headless.MenuItems
      {...props}
      transition
      anchor={anchor}
      className={clsx(
        className,
        // Anchor positioning
        'z-30 [--anchor-gap:--spacing(2)] [--anchor-padding:--spacing(1)] data-[anchor~=end]:[--anchor-offset:6px] data-[anchor~=start]:[--anchor-offset:-6px] sm:data-[anchor~=end]:[--anchor-offset:4px] sm:data-[anchor~=start]:[--anchor-offset:-4px]',
        // Base styles
        'isolate w-max rounded-xl p-1',
        // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
        'outline outline-transparent focus:outline-hidden',
        // Handle scrolling when menu won't fit in viewport
        'overflow-y-auto',
        // Popover background
        'bg-white/75 backdrop-blur-xl dark:bg-gray-800/75',
        // Shadows
        'shadow-lg ring-1 ring-gray-900/10 dark:ring-white/10 dark:ring-inset',
        // Define grid at the menu level if subgrid is supported
        'supports-[grid-template-columns:subgrid]:grid supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]',
        // Transitions
        'transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0',
      )}
    >
      {contextValue ? (
        <SearchableDropdownContext.Provider value={contextValue}>
          <div
            className="col-span-full sticky top-0 z-10 mb-1 rounded-lg px-2 py-1.5 bg-gray-100/80 dark:bg-gray-700/50"
            data-slot="dropdown-search"
          >
            <input
              type="search"
              autoComplete="off"
              aria-label="Filter options"
              placeholder={searchPlaceholder}
              value={contextValue.query}
              onChange={(e) => contextValue.setQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              className={clsx(
                'w-full rounded-md border-0 bg-transparent px-2 py-1 text-sm text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400',
                'focus:outline-hidden focus:ring-2 focus:ring-primary-500/20',
              )}
            />
          </div>
          {props.children as React.ReactNode}
        </SearchableDropdownContext.Provider>
      ) : (
        props.children
      )}
    </Headless.MenuItems>
  )
}

export const DropdownItem = ({
  className,
  searchLabel,
  ...props
}: { className?: string; searchLabel?: string } & (
  | Omit<Headless.MenuItemProps<'button'>, 'as' | 'className'>
  | Omit<Headless.MenuItemProps<typeof Link>, 'as' | 'className'>
)) => {
  const searchContext = useSearchableDropdown()
  const matchesSearch =
    !searchContext ||
    !searchLabel ||
    searchContext.normalizedQuery === '' ||
    searchLabel.toLowerCase().includes(searchContext.normalizedQuery)

  if (!matchesSearch) {
    return null
  }

  const classes = clsx(
    className,
    // Base styles
    'transition-colors group cursor-pointer rounded-lg px-3.5 py-2.5 focus:outline-hidden sm:px-3 sm:py-1.5',
    // Text styles
    'text-left text-base/6 text-gray-900 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]',
    // Focus and hover: use global --dropdown-accent when set by the host, fallback to primary-500
    'data-focus:bg-[var(--dropdown-accent,var(--color-primary-500))] data-focus:text-white',
    'data-hover:bg-[color-mix(in_srgb,var(--dropdown-accent,var(--color-primary-500))_70%,transparent)]! data-hover:text-white',
    // Disabled state
    'data-disabled:opacity-50 data-disabled:cursor-not-allowed',
    // Forced colors mode
    'forced-color-adjust-none forced-colors:data-focus:bg-[Highlight] forced-colors:data-focus:text-[HighlightText] forced-colors:data-focus:*:data-[slot=icon]:text-[HighlightText]',
    // Use subgrid when available but fallback to an explicit grid layout if not
    'col-span-full grid grid-cols-[auto_1fr_1.5rem_0.5rem_auto] items-center supports-[grid-template-columns:subgrid]:grid-cols-subgrid',
    // Icons
    '*:data-[slot=icon]:col-start-1 *:data-[slot=icon]:row-start-1 *:data-[slot=icon]:mr-2.5 *:data-[slot=icon]:-ml-0.5 *:data-[slot=icon]:size-5 sm:*:data-[slot=icon]:mr-2 sm:*:data-[slot=icon]:size-4',
    '*:data-[slot=icon]:text-gray-500 data-focus:*:data-[slot=icon]:text-white data-hover:*:data-[slot=icon]:text-white dark:*:data-[slot=icon]:text-gray-400 dark:data-focus:*:data-[slot=icon]:text-white dark:data-hover:*:data-[slot=icon]:text-white',
    // Avatar
    '*:data-[slot=avatar]:mr-2.5 *:data-[slot=avatar]:-ml-1 *:data-[slot=avatar]:size-6 sm:*:data-[slot=avatar]:mr-2 sm:*:data-[slot=avatar]:size-5',
  )

  return 'to' in props ? (
    <Headless.MenuItem
      as={Link}
      {...props}
      className={classes}
    />
  ) : (
    <Headless.MenuItem
      as="button"
      type="button"
      {...props}
      className={classes}
    />
  )
}

export const DropdownHeader = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => {
  return (
    <div
      {...props}
      className={clsx(className, 'col-span-5 px-3.5 pt-2.5 pb-1 sm:px-3 text-gray-900 dark:text-white')}
    />
  )
}

export const DropdownSection = ({
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuSectionProps, 'as' | 'className'>) => {
  return (
    <Headless.MenuSection
      {...props}
      className={clsx(
        className,
        // Define grid at the section level instead of the item level if subgrid is supported
        'col-span-full supports-[grid-template-columns:subgrid]:grid supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]',
      )}
    />
  )
}

export const DropdownHeading = ({
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuHeadingProps, 'as' | 'className'>) => {
  return (
    <Headless.MenuHeading
      {...props}
      className={clsx(
        className,
        'col-span-full grid grid-cols-[1fr_auto] gap-x-12 px-3.5 pt-2 pb-1 text-sm/5 font-medium text-gray-500 sm:px-3 sm:text-xs/5 dark:text-gray-400',
      )}
    />
  )
}

export const DropdownDivider = ({
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuSeparatorProps, 'as' | 'className'>) => {
  return (
    <Headless.MenuSeparator
      {...props}
      className={clsx(
        className,
        'col-span-full mx-3.5 my-1 h-px border-0 bg-gray-900/5 sm:mx-3 dark:bg-white/10 forced-colors:bg-[CanvasText]',
      )}
    />
  )
}

export const DropdownLabel = ({
  className,
  ...props
}: { className?: string } & Omit<Headless.LabelProps, 'as' | 'className'>) => {
  return (
    <Headless.Label
      {...props}
      data-slot="label"
      className={clsx(className, 'col-start-2 row-start-1')}
      {...props}
    />
  )
}

export const DropdownDescription = ({
  className,
  ...props
}: { className?: string } & Omit<Headless.DescriptionProps, 'as' | 'className'>) => {
  return (
    <Headless.Description
      data-slot="description"
      {...props}
      className={clsx(
        className,
        'col-span-2 col-start-2 row-start-2 text-sm/5 text-gray-500 group-data-focus:text-white sm:text-xs/5 dark:text-gray-400 forced-colors:group-data-focus:text-[HighlightText]',
      )}
    />
  )
}

export const DropdownShortcut = ({
  keys,
  className,
  ...props
}: { keys: string | string[]; className?: string } & Omit<Headless.DescriptionProps<'kbd'>, 'as' | 'className'>) => {
  return (
    <Headless.Description
      as="kbd"
      {...props}
      className={clsx(className, 'col-start-5 row-start-1 flex justify-self-end')}
    >
      {(Array.isArray(keys) ? keys : keys.split('')).map((char, index) => (
        <kbd
          key={index}
          className={clsx([
            'min-w-[2ch] text-center font-sans text-gray-400 capitalize group-data-focus:text-white forced-colors:group-data-focus:text-[HighlightText]',
            // Make sure key names that are longer than one character (like "Tab") have extra space
            index > 0 && char.length > 1 && 'pl-1',
          ])}
        >
          {char}
        </kbd>
      ))}
    </Headless.Description>
  )
}
